import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectExistingHashtag } from "actions/sourceMedia";
import { updateLibraryUrl } from "libs/media-library/url-sync";
import HashtagRow from "./HashtagRow";
import HashtagUsageIndicator from "./HashtagUsageIndicator";
import {
  WEEKLY_HASHTAG_SEARCH_MAX,
  DEFAULT_HASHTAG_FILTER,
} from "constants/MediaLibrary";
import { SELECT_NEW_HASHTAG_ROUTINE } from "constants/ActionTypes";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { cn } from "lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { AddHashtagDialog } from "components/MediaLibrary/modals/AddHashtagDialog";
import type { ReduxRootState } from "types/redux-store";

interface Hashtag {
  hashtag_id: string;
  name: string;
  search_date?: string;
  expired?: boolean;
}

interface Account {
  _id: string;
}

interface ActiveHashtagsProps {
  account: Account;
  hashtags: Hashtag[];
  selectedIgAccount: Account;
}

const ActiveHashtags: React.FC<ActiveHashtagsProps> = ({
  account,
  hashtags,
  selectedIgAccount,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newHashtag, setNewHashtag] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hashtagToAdd, setHashtagToAdd] = useState("");

  const addingHashtag = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [SELECT_NEW_HASHTAG_ROUTINE])
  );

  const atWeeklyMax = hashtags.length >= WEEKLY_HASHTAG_SEARCH_MAX;

  const handleHashtagSelect = (hashtag: Hashtag) => {
    dispatch(
      selectExistingHashtag(account._id, hashtag.hashtag_id, {
        ...DEFAULT_HASHTAG_FILTER,
        excludeAccounts: [selectedIgAccount._id],
      })
    );

    // Update URL with selected hashtag
    const newParams = updateLibraryUrl(searchParams, {
      tab: "hashtags",
      account: account._id,
      hashtag: hashtag.hashtag_id,
    });
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const handleAddHashtag = () => {
    let hashtag = newHashtag.trim();

    if (hashtag.length === 0) {
      return;
    }

    // If they add the # in front let's remove it
    if (hashtag[0] === "#") {
      hashtag = hashtag.slice(1);
    }

    setHashtagToAdd(hashtag);
    setDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    setNewHashtag("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddHashtag();
    }
  };

  return (
    <>
      <div className="space-y-2">
        {/* Progress Indicator */}
        <HashtagUsageIndicator activeCount={hashtags.length} />

        {/* Inline Search Input */}
        <div className="px-3">
          <Tooltip>
            <TooltipTrigger asChild disabled={!atWeeklyMax}>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">
                    #
                  </span>
                  <Input
                    placeholder="Add hashtag..."
                    value={newHashtag}
                    onChange={(e) => setNewHashtag(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={cn(
                      "h-9 pl-7 text-sm border-neutral-200 bg-white",
                      atWeeklyMax && "opacity-50"
                    )}
                    disabled={atWeeklyMax}
                    maxLength={50}
                  />
                </div>
                <Button
                  variant="black-primary"
                  size="sm"
                  className="h-9 px-3"
                  onClick={handleAddHashtag}
                  disabled={addingHashtag || !newHashtag.trim() || atWeeklyMax}
                >
                  {addingHashtag ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <i className="fa-solid fa-arrow-right" />
                  )}
                </Button>
              </div>
            </TooltipTrigger>
            {atWeeklyMax && (
              <TooltipContent className="max-w-[220px] text-left font-normal">
                Instagram only allows 30 hashtags to be searched within a 7 day
                period. Please wait for a hashtag to expire or choose another
                account.
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        {/* Hashtag List - no wrapper header */}
        {hashtags.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {hashtags.map((hashtag) => (
              <HashtagRow
                key={hashtag.hashtag_id}
                hashtag={hashtag}
                onClick={() => handleHashtagSelect(hashtag)}
              />
            ))}
          </div>
        )}
      </div>

      <AddHashtagDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        hashtagName={hashtagToAdd}
        account={selectedIgAccount}
        activeHashtagsCount={hashtags.length}
        title="Add Hashtag"
        onSuccess={handleDialogSuccess}
      />
    </>
  );
};

export default ActiveHashtags;
